import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate, Navigate } from "react-router-dom";
import {
  Button,
  Table,
} from "@commonwealthdevelopment/web-ui-component-library";
import { Header, Body, LinkCopy } from "../../components";
import styles from "./LinkDetails.module.css";
import { toast } from "react-toastify";
import useFetchWithMsal from "../../hooks/useFetchWithMsal";
import {
  allowedGroups,
  useAuthorization,
} from "../../contexts/AuthorizationContext";

export const LinkDetails = () => {
  const { groups } = useAuthorization();
  const [data, setData] = useState(null);
  const [loading, setIsLoading] = useState(true);
  const { id } = useParams();
  const navigate = useNavigate();
  const { error, execute } = useFetchWithMsal();

  const fetchData = useCallback(() => {
    execute("GET", `AccessLinks/${id}`).then((response) => {
      setData(response);
      setIsLoading(false);
    });
  }, [execute, id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDeleteClick = async () => {
    await toast.promise(execute("DELETE", `AccessLinks/${id}`), {
      pending: {
        render() {
          return "Revoking Authentication Link";
        },
      },
      success: {
        render({ data }) {
          return `Authentication link revoked successfully`;
        },
        icon: "🟢",
      },
      error: {
        render({ data }) {
          return data.message;
        },
      },
    });
    fetchData();
  };

  if (
    data &&
    data.email &&
    !groups.some((g) => g.value === allowedGroups.NLS.value)
  )
    return <Navigate to="/" replace />;

  if (
    data &&
    !data.email &&
    !groups.some((g) => g.value === allowedGroups.PROSPECT.value)
  )
    return <Navigate to="/" replace />;

  return (
    <>
      <Header>
        <h1>Authentication Link Details</h1>
      </Header>

      <Body>
        {loading || !data ? (
          <h1>Loading...</h1>
        ) : (
          <>
            <div className={styles.header}>
              <>
                {data && data.status === "Active" && (
                  <Button
                    automationId="story_button"
                    iconSize="2em"
                    iconType="minus-circle"
                    onClick={handleDeleteClick}
                    showIcon
                  >
                    Revoke link
                  </Button>
                )}
                <Button
                  automationId="story_button"
                  onClick={() => navigate(`/links/${id}/history`)}
                >
                  History
                </Button>
              </>
            </div>

            <p style={{ marginTop: "1.5rem" }}>
              <small>
                Authentication Link created for{" "}
                <strong>{data.email ? "end user" : "affiliate"}</strong>
              </small>
            </p>

            {data && (
              <Table>
                <tbody>
                  <tr>
                    <td>ID</td>
                    <td>{id}</td>
                  </tr>
                  <tr>
                    <td>Expires At</td>
                    <td>{data.expireAt}</td>
                  </tr>
                  <tr>
                    <td>Created At</td>
                    <td>{data.createdAt}</td>
                  </tr>
                  <tr>
                    <td>Created By</td>
                    <td>{data.createdBy}</td>
                  </tr>
                  <tr>
                    <td>Notes</td>
                    <td>{data.notes}</td>
                  </tr>

                  {!data.email ? (
                    <tr>
                      <td>Affiliate Name</td>
                      <td>{data.prospectName}</td>
                    </tr>
                  ) : (
                    <tr>
                      <td>Email</td>
                      <td>{data.email}</td>
                    </tr>
                  )}

                  {data.deletedAt && (
                    <>
                      <tr>
                        <td>Revoked At</td>
                        <td>
                          {data.deletedAt ? (
                            <div style={{ color: "red" }}>{data.deletedAt}</div>
                          ) : null}
                        </td>
                      </tr>
                      <tr>
                        <td>Revoked By</td>
                        <td>
                          {data.deletedBy ? (
                            <div style={{ color: "red" }}>{data.deletedBy}</div>
                          ) : null}
                        </td>
                      </tr>
                    </>
                  )}

                  <tr>
                    <td>Status</td>
                    <td>
                      {data.status === "Expired" ||
                      data.status === "Revoked" ? (
                        <div style={{ color: "red" }}>{data.status}</div>
                      ) : (
                        <div style={{ color: "green" }}>{data.status}</div>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>Was used before</td>
                    <td>{data.wasAccessedBefore ? "Yes" : "No"}</td>
                  </tr>
                </tbody>
              </Table>
            )}

            {data &&
              data.status === "Active" &&
              (!data.email || (data.email && !data.wasAccessedBefore)) && (
                <LinkCopy link={data.link} />
              )}
          </>
        )}

        <Link to="/links">Back to Links</Link>
      </Body>
    </>
  );
};
